import * as api from '../api';
import { SNACKBAR_DURATION_MS, getSnackbarColor } from "../utils/snackbar";

// Action Types
export const GET_CLIENT_PORTAL_DATA = 'GET_CLIENT_PORTAL_DATA';
export const DOWNLOAD_CLIENT_DOCUMENT = 'DOWNLOAD_CLIENT_DOCUMENT';
export const SEND_CLIENT_MESSAGE = 'SEND_CLIENT_MESSAGE';
export const MAKE_CLIENT_PAYMENT = 'MAKE_CLIENT_PAYMENT';
export const GET_CLIENT_MESSAGES = 'GET_CLIENT_MESSAGES';
export const GET_CLIENT_INVOICES = 'GET_CLIENT_INVOICES';
export const GET_CLIENT_APPOINTMENTS = 'GET_CLIENT_APPOINTMENTS';
export const MARK_MESSAGE_READ = 'MARK_MESSAGE_READ';
export const SET_CLIENT_PORTAL_LOADING = 'SET_CLIENT_PORTAL_LOADING';
export const GET_CONSTRUCTION_PORTAL_DATA = 'GET_CONSTRUCTION_PORTAL_DATA';
export const GET_HEALTHCARE_PORTAL_DATA = 'GET_HEALTHCARE_PORTAL_DATA';
export const GET_ACCOUNTING_PORTAL_DATA = 'GET_ACCOUNTING_PORTAL_DATA';
export const GET_CLIENT_SETTINGS = 'GET_CLIENT_SETTINGS';
export const UPDATE_CLIENT_SETTINGS = 'UPDATE_CLIENT_SETTINGS';

// Get client portal data
export const getClientPortalData = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });

    const { data } = await api.fetchClientPortalData();

    dispatch({
      type: GET_CLIENT_PORTAL_DATA,
      payload: data
    });

    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return data;
  } catch (error) {
    console.error('Error fetching client portal data:', error);
    if (openSnackbar) {
      openSnackbar('Error loading portal data', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading portal data"));
    }
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Download client document
export const downloadClientDocument = (documentId, openSnackbar) => async (dispatch) => {
  try {
    const { data } = await api.downloadClientDocument(documentId);

    dispatch({
      type: DOWNLOAD_CLIENT_DOCUMENT,
      payload: { documentId, data }
    });

    if (openSnackbar) {
      openSnackbar('Document download started', SNACKBAR_DURATION_MS, getSnackbarColor("Document download started"));
    }

    return data;
  } catch (error) {
    console.error('Error downloading client document:', error);
    if (openSnackbar) {
      openSnackbar('Error downloading document', SNACKBAR_DURATION_MS, getSnackbarColor("Error downloading document"));
    }
    throw error;
  }
};

// Send client message
export const sendClientMessage = (messageData, openSnackbar) => async (dispatch) => {
  try {
    const { data } = await api.sendClientMessage(messageData);

    dispatch({
      type: SEND_CLIENT_MESSAGE,
      payload: data
    });

    if (openSnackbar) {
      openSnackbar('Message sent successfully', SNACKBAR_DURATION_MS, getSnackbarColor("Message sent successfully"));
    }

    return data;
  } catch (error) {
    console.error('Error sending client message:', error);
    if (openSnackbar) {
      openSnackbar('Error sending message', SNACKBAR_DURATION_MS, getSnackbarColor("Error sending message"));
    }
    throw error;
  }
};

// Make client payment
export const makeClientPayment = (paymentData, openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });

    const { data } = await api.makeClientPayment(paymentData);

    dispatch({
      type: MAKE_CLIENT_PAYMENT,
      payload: data
    });

    if (openSnackbar) {
      openSnackbar('Payment processed successfully', SNACKBAR_DURATION_MS, getSnackbarColor("Payment processed successfully"));
    }

    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return data;
  } catch (error) {
    console.error('Error processing client payment:', error);
    if (openSnackbar) {
      openSnackbar('Error processing payment', SNACKBAR_DURATION_MS, getSnackbarColor("Error processing payment"));
    }
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Get client messages - extracted from portal data
export const getClientMessages = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });

    // Use the main portal data endpoint which includes messages
    const { data } = await api.fetchClientPortalData();

    dispatch({
      type: GET_CLIENT_MESSAGES,
      payload: data.messages || []
    });

    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return data.messages || [];
  } catch (error) {
    console.error('Error fetching client messages:', error);
    if (openSnackbar) {
      openSnackbar('Error loading messages', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading messages"));
    }
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Get client invoices - extracted from portal data
export const getClientInvoices = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });

    // Use the main portal data endpoint which includes invoices
    const { data } = await api.fetchClientPortalData();

    dispatch({
      type: GET_CLIENT_INVOICES,
      payload: data.invoices || []
    });

    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return data.invoices || [];
  } catch (error) {
    console.error('Error fetching client invoices:', error);
    if (openSnackbar) {
      openSnackbar('Error loading invoices', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading invoices"));
    }
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Get client appointments - extracted from portal data
export const getClientAppointments = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });

    // Use the main portal data endpoint which includes appointments
    const { data } = await api.fetchClientPortalData();

    dispatch({
      type: GET_CLIENT_APPOINTMENTS,
      payload: data.appointments || []
    });

    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return data.appointments || [];
  } catch (error) {
    console.error('Error fetching client appointments:', error);
    if (openSnackbar) {
      openSnackbar('Error loading appointments', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading appointments"));
    }
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Mark message as read - using send message endpoint to update
export const markMessageRead = (messageId, openSnackbar) => async (dispatch) => {
  try {
    // Send a read receipt message
    const { data } = await api.sendClientMessage({
      messageId,
      action: 'mark_read'
    });

    dispatch({
      type: MARK_MESSAGE_READ,
      payload: { messageId, data }
    });

    return data;
  } catch (error) {
    console.error('Error marking message as read:', error);
    if (openSnackbar) {
      openSnackbar('Error updating message status', SNACKBAR_DURATION_MS, getSnackbarColor("Error updating message status"));
    }
    throw error;
  }
};

// Get Construction Portal Data
export const getConstructionPortalData = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });
    
    // Placeholder data for now - replace with: await api.fetchConstructionPortalData();
    const placeholderData = {
      projects: [{_id: '1', projectName: 'Office Building Renovation', projectNumber: 'PROJ-001', status: 'In Progress', progress: 65, startDate: new Date('2025-01-15'), estimatedCompletion: new Date('2025-06-30'), budget: 250000, spent: 162500}],
      tasks: [{_id: '1', taskName: 'Foundation Work', status: 'Completed', dueDate: new Date('2025-02-28'), assignedTo: 'Team A'}, {_id: '2', taskName: 'Electrical Installation', status: 'In Progress', dueDate: new Date('2025-04-15'), assignedTo: 'Team B'}],
      documents: [{_id: '1', title: 'Project Blueprint', fileName: 'blueprint_v2.pdf', uploadedDate: new Date('2025-01-20'), category: 'Plans'}, {_id: '2', title: 'Building Permit', fileName: 'permit.pdf', uploadedDate: new Date('2025-01-10'), category: 'Legal'}],
      invoices: [{_id: '1', invoiceNumber: 'INV-001', amount: 50000, status: 'Paid', dueDate: new Date('2025-02-01')}, {_id: '2', invoiceNumber: 'INV-002', amount: 75000, status: 'Pending', dueDate: new Date('2025-03-01')}]
    };

    dispatch({type: GET_CONSTRUCTION_PORTAL_DATA, payload: placeholderData});
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return placeholderData;
  } catch (error) {
    console.error('Error fetching construction portal data:', error);
    if (openSnackbar) openSnackbar('Error loading construction portal data', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading construction portal data"));
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Get Healthcare Portal Data
export const getHealthcarePortalData = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });
    
    const placeholderData = {
      appointments: [{_id: '1', appointmentType: 'General Checkup', doctor: 'Dr. Smith', date: new Date('2025-10-20'), time: '10:00 AM', status: 'Scheduled', location: 'Clinic A - Room 203'}, {_id: '2', appointmentType: 'Follow-up', doctor: 'Dr. Johnson', date: new Date('2025-11-05'), time: '2:30 PM', status: 'Scheduled', location: 'Clinic B - Room 105'}],
      records: [{_id: '1', recordType: 'Lab Results', title: 'Blood Test Results', date: new Date('2025-09-15'), doctor: 'Dr. Smith', summary: 'All values within normal range'}, {_id: '2', recordType: 'Consultation', title: 'Annual Physical Exam', date: new Date('2025-08-20'), doctor: 'Dr. Johnson', summary: 'Patient in good health'}],
      prescriptions: [{_id: '1', medicationName: 'Amoxicillin 500mg', prescribedBy: 'Dr. Smith', prescribedDate: new Date('2025-09-15'), dosage: '1 tablet twice daily', duration: '7 days', status: 'Active'}, {_id: '2', medicationName: 'Vitamin D3', prescribedBy: 'Dr. Johnson', prescribedDate: new Date('2025-08-20'), dosage: '1 tablet daily', duration: 'Ongoing', status: 'Active'}],
      invoices: [{_id: '1', invoiceNumber: 'MED-001', serviceDescription: 'General Checkup', amount: 150, status: 'Paid', date: new Date('2025-09-15')}, {_id: '2', invoiceNumber: 'MED-002', serviceDescription: 'Lab Tests', amount: 250, status: 'Pending', date: new Date('2025-10-01')}]
    };

    dispatch({type: GET_HEALTHCARE_PORTAL_DATA, payload: placeholderData});
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return placeholderData;
  } catch (error) {
    console.error('Error fetching healthcare portal data:', error);
    if (openSnackbar) openSnackbar('Error loading healthcare portal data', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading healthcare portal data"));
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};

// Get Accounting Portal Data
export const getAccountingPortalData = (openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: true });
    
    const placeholderData = {
      invoices: [{_id: '1', invoiceNumber: 'INV-2025-001', description: 'Monthly Services - September', amount: 5000, status: 'Paid', issueDate: new Date('2025-09-01'), dueDate: new Date('2025-09-15')}, {_id: '2', invoiceNumber: 'INV-2025-002', description: 'Monthly Services - October', amount: 5000, status: 'Pending', issueDate: new Date('2025-10-01'), dueDate: new Date('2025-10-15')}],
      payments: [{_id: '1', paymentNumber: 'PAY-001', invoiceNumber: 'INV-2025-001', amount: 5000, paymentMethod: 'Bank Transfer', paymentDate: new Date('2025-09-10'), status: 'Completed'}],
      summary: {totalOutstanding: 5000, totalPaid: 5000, overdueAmount: 0}
    };

    dispatch({type: GET_ACCOUNTING_PORTAL_DATA, payload: placeholderData});
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    return placeholderData;
  } catch (error) {
    console.error('Error fetching accounting portal data:', error);
    if (openSnackbar) openSnackbar('Error loading accounting portal data', SNACKBAR_DURATION_MS, getSnackbarColor("Error loading accounting portal data"));
    dispatch({ type: SET_CLIENT_PORTAL_LOADING, payload: false });
    throw error;
  }
};
